import {
  LayoutDashboard,
  Building2,
  Building,
  Megaphone,
  FileText,
  DollarSign,
  BarChart3,
  Settings,
  Moon,
  Sun,
  Layers,
  MessageCircle,
  Users,
  Share2,
  Gift,
  ImageUp,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { useMainCompanyId } from "@/hooks/useMainCompanyId";
import { usePermissions } from "@/hooks/usePermissions";
import { useMediaReviewQueue } from "@/hooks/useMediaCapture";
import { useKycComplianceAccess } from "@/hooks/useKycCompliance";
import type { PermissionKey, GranularPermissionKey, SpecialPermissionKey } from "@/lib/rbac";
import logoFull from "@/assets/logo-full.png";
import logoIcon from "@/assets/logo-icon.png";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";

interface MenuItem {
  titleAr: string;
  titleEn: string;
  url: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
  mainCompanyOnly?: boolean;
  permission?: PermissionKey | GranularPermissionKey | SpecialPermissionKey;
  /** Shown only to an EXPLICIT `compliance.review` holder — admins don't pass (useKycComplianceAccess). */
  complianceGrant?: boolean;
}

const MEDIA_REVIEW_URL = "/dashboard/media-review";

export const dashboardMenuItems: MenuItem[] = [
  {
    titleAr: "لوحة التحكم",
    titleEn: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard
  },
  {
    // Add-property requests are gone (owners add properties directly, CEO 2026-07);
    // "Service Requests" is now the single request surface. The legacy
    // /dashboard/requests route stays for notification deep-links, but is off the nav.
    titleAr: "طلبات الخدمة",
    titleEn: "Service Requests",
    url: "/dashboard/service-requests",
    icon: Layers,
    permission: "service_requests.view",
  },
  {
    titleAr: "المحادثات",
    titleEn: "Conversations",
    url: "/dashboard/conversations",
    icon: MessageCircle,
  },
  {
    titleAr: "العقارات",
    titleEn: "Properties",
    url: "/dashboard/properties",
    icon: Building2,
    permission: "properties.view",
  },
  {
    titleAr: "العقود",
    titleEn: "Contracts",
    url: "/dashboard/contracts",
    icon: FileText,
    permission: "contracts.view",
  },
  {
    titleAr: "الإعلانات",
    titleEn: "Ads",
    url: "/dashboard/ads",
    icon: Megaphone,
    permission: "advertisements.view",
  },
  {
    // The domain/permission key stays `projects`; the route is the shared
    // cross-portal Opportunities surface.
    titleAr: "الفرص",
    titleEn: "Opportunities",
    url: "/dashboard/opportunities",
    icon: Building,
    permission: "projects.view",
  },
  {
    // Nav to the Leads address book (Farah's CRM demand surface). Off-plan
    // project interest/bookings now flow into the same Lead/Interest pipeline,
    // so this single entry covers all demand — there is no separate projects
    // "interested" page. Gated by the CRM's permission (service_requests.*).
    titleAr: "دفتر العملاء",
    titleEn: "Address Book",
    url: "/dashboard/leads",
    icon: Users,
    permission: "service_requests.view",
  },
  {
    titleAr: "المالية",
    titleEn: "Finance",
    url: "/dashboard/finance",
    icon: DollarSign,
    permission: "finance.view",
  },
  {
    titleAr: "التقارير",
    titleEn: "Reports",
    url: "/dashboard/reports",
    icon: BarChart3,
    permission: "reports.view",
  },
  {
    // Admin oversight of employee referral links. Gated on employees.* (referrals
    // are an employee-management surface — see AdminReferralsController).
    titleAr: "الإحالات",
    titleEn: "Referrals",
    url: "/dashboard/referrals",
    icon: Share2,
    permission: "employees.view",
  },
  {
    // Every dashboard user — admins included — can refer owners and earn the
    // referral commission: their own link, collaborators and earnings.
    titleAr: "إحالاتي",
    titleEn: "My referrals",
    url: "/dashboard/my-referrals",
    icon: Gift,
  },
  {
    // PR 4b · reviewers (special `media.approve`; admins pass) — badge = visits in review.
    titleAr: "مراجعة الوسائط",
    titleEn: "Media review",
    url: MEDIA_REVIEW_URL,
    icon: ImageUp,
    permission: "media.approve",
  },
  {
    // PR 6b · the compliance officer's KYC queue (explicit grant only).
    titleAr: "مراجعة اعرف عميلك",
    titleEn: "KYC review",
    url: "/dashboard/compliance/kyc",
    icon: ShieldCheck,
    complianceGrant: true,
  },
  {
    titleAr: "الإعدادات",
    titleEn: "Settings",
    url: "/dashboard/settings",
    icon: Settings,
    permission: "settings.view",
  },
];

export function AppSidebar() {
  const { t, direction } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { isAdmin, companyId, activeRole } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const { mainCompanyId } = useMainCompanyId();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = !isMobile && state === "collapsed";

  // Active role is on the main Dallali RE company (staff-level role)
  const isMainCompanyStaff = mainCompanyId
    ? companyId === mainCompanyId &&
        ["admin", "employee", "owner"].includes(activeRole ?? "")
    : false;

  const handleNavClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const canReviewMedia = !permissionsLoading && hasPermission("media.approve");
  const { data: reviewQueue } = useMediaReviewQueue({ enabled: canReviewMedia });
  const pendingReviews = canReviewMedia ? (reviewQueue?.length ?? 0) : 0;

  const { review: canReviewKyc } = useKycComplianceAccess();

  const visibleMenuItems = dashboardMenuItems.filter(
    (item) =>
      (!item.complianceGrant || canReviewKyc) &&
      (!item.adminOnly || isAdmin) &&
      (!item.mainCompanyOnly || isMainCompanyStaff) &&
      (!item.permission || permissionsLoading || hasPermission(item.permission))
  );

  return (
    <Sidebar 
      className="border-0 bg-sidebar text-sidebar-foreground"
      collapsible="icon"
      side="start"
    >
      <SidebarHeader className="p-4 border-b border-sidebar-border">
        <Link to="/" className="flex items-center justify-center cursor-pointer">
          {isCollapsed ? (
            <img
              src={logoIcon}
              alt="Dallali"
              className="w-8 h-8 object-contain"
            />
          ) : (
            <img
              src={logoFull}
              alt="Dallali"
              className="h-10 object-contain"
            />
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMenuItems.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton 
                    asChild
                    tooltip={isCollapsed ? t(item.titleAr, item.titleEn) : undefined}
                    className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  >
                    <NavLink 
                      to={item.url} 
                      end={item.url === '/dashboard'}
                      onClick={handleNavClick}
                      className={cn(
                        "flex items-center gap-3 rounded-lg transition-colors",
                        isCollapsed ? "justify-center w-full h-full" : "px-3 py-2.5"
                      )}
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-semibold dark:text-warning"
                    >
                      <span className="relative shrink-0">
                        <item.icon className="h-5 w-5" />
                        {isCollapsed && item.url === MEDIA_REVIEW_URL && pendingReviews > 0 && (
                          <span className="absolute -end-1 -top-1 h-2 w-2 rounded-full bg-destructive" aria-hidden />
                        )}
                      </span>
                      {!isCollapsed && (
                        <span>{t(item.titleAr, item.titleEn)}</span>
                      )}
                      {!isCollapsed && item.url === MEDIA_REVIEW_URL && pendingReviews > 0 && (
                        <span
                          className="ms-auto rounded-full bg-destructive px-1.5 py-0.5 text-[11px] font-semibold leading-none text-destructive-foreground"
                          data-testid="media-review-badge"
                          aria-label={t("زيارات بانتظار المراجعة", "Visits waiting for review")}
                          dir="ltr"
                        >
                          {pendingReviews}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2 border-t border-sidebar-border mt-auto">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size={isCollapsed ? "icon" : "default"}
          onClick={toggleTheme}
          className={cn(
            "w-full h-10 text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground",
            isCollapsed ? "justify-center" : "justify-start gap-3 px-3"
          )}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5 shrink-0" />
          ) : (
            <Moon className="h-5 w-5 shrink-0" />
          )}
          {!isCollapsed && (
            <span>{theme === 'dark' ? t("الوضع النهاري", "Light Mode") : t("الوضع الليلي", "Dark Mode")}</span>
          )}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
